/**
 * Readlang Premium bought in the iOS app, from the App Store (the app's
 * paywall, PaywallView.swift, through the bridge). Each purchase names the
 * user's Readlang account, and each transaction goes to the server, which
 * decides access (src/appStore.js), before the app tells the App Store it's
 * delivered.
 */
import * as genericModals from "genericModals";
import * as iOSBridge from "iOSBridge";
import readlangClient, { requestErrorMessage } from "readlangClient";
import * as appStoreProducts from "shared/appStoreProducts.js";
import * as planFeatures from "shared/planFeatures";
import * as signupModal from "signupModal";
import stores from "stores/stores";

export interface StoreTransaction {
  id: string;
  signedTransaction: string;
  signedRenewalInfo?: string;
}

export type SignInMethod = "password" | "google" | "apple";

/** Names the user's Readlang account in their purchases */
const accountToken = async () =>
  (
    await readlangClient.request<{ accountToken: string }>(
      "GET",
      "/api/appStore/accountToken",
      {}
    )
  ).data.accountToken;

/** Records a transaction on the server, then finishes it with the App Store */
const deliver = async ({
  id,
  signedTransaction,
  signedRenewalInfo,
}: StoreTransaction) => {
  await readlangClient.request("POST", "/api/appStore/purchases", {
    signedTransaction,
    signedRenewalInfo,
  });
  iOSBridge.get()?.finishTransaction(id);
};

/**
 * The Readlang account the Apple Account's subscription belongs to, when it's
 * another one: an Apple Account can't have a second subscription, so a
 * purchase would only change that account's.
 */
const otherAccountSubscribed = async (app: iOSBridge.Bridge) => {
  const subscription = await app.currentSubscription();
  if (!subscription) {
    return undefined;
  }
  const { data } = await readlangClient.request<{
    email?: string;
    signInMethods?: SignInMethod[];
  }>("POST", "/api/appStore/owner", {
    signedTransaction: subscription.signedTransaction,
  });
  return data.email ? data : undefined;
};

/** Offers to sign in to the account that has the Apple Account's subscription */
const offerSignIn = (owner: {
  email?: string;
  signInMethods?: SignInMethod[];
}) =>
  genericModals.confirm({
    title: "Already subscribed",
    message: `This Apple Account's Readlang Premium subscription is for another Readlang account, ${owner.email}.`,
    ok: `Sign in as ${owner.email}`,
    cancel: "OK",
    callback: (result) => {
      if (result === "ok") {
        readlangClient.logout(() => {
          window.location.href =
            "/?" +
            new URLSearchParams({
              signInAs: owner.email!,
              methods: owner.signInMethods!.join(","),
            });
        });
      }
    },
  });

/**
 * The app's paywall (PaywallView.swift). Once a subscription bought or
 * restored there reaches the server, a fresh page load welcomes the user to
 * Premium, everywhere in the app. Whether one was. Never rejects: what goes
 * wrong is shown to the user.
 */
export const showPaywall = async (options: {
  /** Why to upgrade; otherwise each plan's own name */
  heading?: string;
  subheading?: string;
}): Promise<boolean> => {
  const app = iOSBridge.get();
  if (!app) {
    return false;
  }
  if (await readlangClient.isLocalMode()) {
    // A purchase names the account it's for
    signupModal.show({ heading: "Sign in to go Premium", stores });
    return false;
  }
  let transactions: StoreTransaction[] | null;
  try {
    const owner = await otherAccountSubscribed(app);
    if (owner) {
      offerSignIn(owner);
      return false;
    }
    transactions = await app.showPaywall({
      ...options,
      accountToken: await accountToken(),
      plans: planFeatures.plans.map(({ name, features, permissions }) => ({
        name,
        features,
        productIDs: appStoreProducts.products
          .filter((product) => product.permissions === permissions)
          .map((product) => product.productID),
      })),
    });
  } catch (error) {
    genericModals.alert({ message: requestErrorMessage(error) });
    return false;
  }
  if (!transactions?.length) {
    return false;
  }
  try {
    for (const transaction of transactions) {
      await deliver(transaction);
    }
  } catch (error) {
    // Paid, so the App Store has it: Restore, or the app's next launch, sends
    // it again
    genericModals.alert({
      title: "Purchase not linked yet",
      message: `Your purchase went through, but Readlang couldn't link it to your account (${requestErrorMessage(error)}). Open the plans again and tap Restore to try again.`,
    });
    return false;
  }
  window.location.pathname = "/upgraded";
  return true;
};

/** Apple's own screen for managing the subscription */
export const manageSubscriptions = () => iOSBridge.get()?.manageSubscriptions();

/**
 * Delivers transactions arriving outside a purchase: renewals, a parent's
 * approval, a purchase on another device... and those left unfinished, which
 * the app hands over once the page is ready for them.
 */
export const listen = () => {
  const deliverIfSignedIn = async (transaction: StoreTransaction) => {
    if (await readlangClient.isLocalMode()) {
      // Signed out: left unfinished until a later launch
      return;
    }
    await deliver(transaction).catch((error) =>
      console.error("App Store transaction not delivered", error)
    );
  };
  window.READLANG.appStoreTransaction = deliverIfSignedIn;
  void (async () => {
    try {
      for (const transaction of (await iOSBridge
        .get()
        ?.unfinishedTransactions()) ?? []) {
        await deliverIfSignedIn(transaction);
      }
    } catch (error) {
      console.error("Unfinished App Store transactions not read", error);
    }
  })();
};
